import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Import images
import heroImage0 from '../../assets/herocover/0.jpg';
import heroImage1 from '../../assets/herocover/1.jpg';
import heroImage2 from '../../assets/herocover/2.jpg';
import heroImage3 from '../../assets/herocover/3.jpg';
import heroImage4 from '../../assets/herocover/4.jpg';
import heroImage5 from '../../assets/herocover/5.jpg';
import heroImage6 from '../../assets/herocover/6.jpg';
import heroImage7 from '../../assets/herocover/7.jpg';
import heroImage8 from '../../assets/herocover/8.jpg';

// Array of imported background images
const backgroundImages = [
  heroImage0,
  heroImage1,
  heroImage2,
  heroImage3,
  heroImage4,
  heroImage5,
  heroImage6,
  heroImage7,
  heroImage8,
];
// CSS Variables
const gold = '#DBAE6E';
const darkGold = '#B58B4C';
const fallbackImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const Hero = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Preload images
  useEffect(() => {
    let loadedCount = 0;
    backgroundImages.forEach((src, index) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        loadedCount++;
        if (loadedCount >= 1) setIsImageLoaded(true);
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount >= 1) setIsImageLoaded(true);
        backgroundImages[index] = fallbackImage;
      };
    });
    const timeout = setTimeout(() => setIsImageLoaded(true), 3000);
    return () => clearTimeout(timeout);
  }, []);

  // Auto-rotate images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleExploreClick = useCallback(() => navigate('/shop'), [navigate]);

  // Animation variants
  const variants = useMemo(() => ({
    background: {
      initial: { opacity: 0, scale: 1.1 },
      animate: { opacity: 1, scale: 1, transition: { duration: 1, ease: [0.4, 0, 0.2, 1] } },
      exit: { opacity: 0, scale: 1.1, transition: { duration: 1, ease: [0.4, 0, 0.2, 1] } },
    },
    content: {
      initial: { opacity: 0, y: 50 },
      animate: { opacity: 1, y: 0, transition: { duration: 0.8, staggerChildren: 0.2 } },
    },
    button: {
      initial: { opacity: 0, scale: 0.9 },
      animate: { opacity: 1, scale: 1, transition: { duration: 0.6 } },
      hover: {
        scale: 1.05,
        backgroundImage: `linear-gradient(to bottom right, ${gold}, ${darkGold})`,
        boxShadow: `0 8px 32px rgba(219, 174, 110, 0.5)`,
        transition: { duration: 0.3, type: 'spring', stiffness: 150 },
      },
      tap: { scale: 0.95 },
    },
    card: (index) => {
      const totalCards = backgroundImages.length;
      const relativeIndex = (index - currentIndex + totalCards) % totalCards;
      const isVisible = relativeIndex < 3;
      const position = relativeIndex;

      return {
        x: isVisible ? (position - 1) * 160 : 800, // Tighter spacing
        y: isVisible ? 0 : 100,
        rotateY: isVisible ? (position - 1) * 8 : 0, // Subtle 3D tilt
        opacity: isVisible ? (position === 1 ? 1 : 0.9) : 0,
        scale: position === 1 ? 1.2 : 0.95,
        zIndex: position === 1 ? 30 : 20 - position,
        boxShadow: position === 1
          ? `0 15px 30px rgba(219, 174, 110, 0.6)`
          : `0 8px 16px rgba(0, 0, 0, 0.4)`,
        transition: {
          type: 'spring',
          stiffness: 130,
          damping: 14,
          mass: 0.7,
          opacity: { duration: 0.3 },
        },
      };
    },
  }), [currentIndex]);

  return (
    <section className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-primary dark:bg-primary-dark font-montserrat">
      {/* Loading Overlay */}
      <AnimatePresence>
        {!isImageLoaded && (
          <motion.div
            className="absolute inset-0 bg-primary dark:bg-primary-dark flex items-center justify-center z-50"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="w-10 h-10 border-4 border-t-[#DBAE6E] border-r-[#DBAE6E] border-b-transparent border-l-transparent rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Layout: Left Content, Right Cards */}
      {isImageLoaded && (
        <div className="hidden lg:flex w-full max-w-7xl mx-auto h-screen items-center">
          {/* Left: Content */}
          <motion.div
            className="w-1/2 pl-12 pr-8 flex flex-col justify-center space-y-8 z-10"
            variants={variants.content}
            initial="initial"
            animate="animate"
          >
            <motion.h1
              className="text-5xl lg:text-7xl font-extrabold font-playfair uppercase tracking-wider text-[#DBAE6E]"
              variants={variants.content}
            >
              Discover Timeless Elegance
            </motion.h1>
            <motion.p
              className="text-lg lg:text-2xl font-light text-black dark:text-white max-w-md"
              variants={variants.content}
            >
              Laynered offers a collection of unique & premium Outfits with elegant and vibe matching designs.            </motion.p>
            <motion.button
              className="px-12 py-5 bg-[#DBAE6E] text-black font-semibold text-lg uppercase tracking-wider rounded-full flex items-center gap-4 border-2 border-[#DBAE6E] shadow-2xl focus:outline-none focus:ring-2 focus:ring-[#DBAE6E] w-fit"
              variants={variants.button}
              initial="initial"
              animate="animate"
              whileHover="hover"
              whileTap="tap"
              onClick={handleExploreClick}
              aria-label="Explore all products"
            >
              Buy Laynered
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </motion.button>


          </motion.div>



          {/* Right: Card View */}
          <div className="w-1/2 flex items-center justify-center relative z-0">
            <div className="relative w-full h-[80%] flex items-center justify-center">
              {backgroundImages.map((image, index) => (
                <motion.div
                  className="absolute w-60 h-96 rounded-3xl overflow-hidden border-2 border-[#DBAE6E]/30"
                  key={`card-${index}`}
                  animate={variants.card(index)}
                  style={{ transformStyle: 'preserve-3d', transformPerspective: 1200 }}
                  whileHover={{
                    scale: variants.card(index).scale * 1.1,
                    rotateY: variants.card(index).rotateY * 1.3,
                    boxShadow: `0 20px 40px rgba(219, 174, 110, 0.7)`,
                    transition: { duration: 0.3 },
                  }}
                >
                  <img
                    src={image}
                    alt={`Hero image ${index + 1}`}
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                    onError={(e) => (e.target.src = fallbackImage)}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50" />
                  {variants.card(index).zIndex === 30 && (
                    <motion.div
                      className="absolute inset-0 border-4 border-[#DBAE6E]/60 rounded-3xl"
                      animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.03, 1] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile View: Single Image */}
      <AnimatePresence>
        {isImageLoaded && (
          <motion.div
            className="lg:hidden absolute inset-0 z-0"
            key={`mobile-${currentIndex}`}
            variants={variants.background}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <img
              src={backgroundImages[currentIndex]}
              alt={`Hero background ${currentIndex + 1}`}
              className="w-full h-full object-cover object-center"
              loading="lazy"
              onError={(e) => (e.target.src = fallbackImage)}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/60" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Content */}
      {isImageLoaded && (
        <motion.div
          className="lg:hidden absolute z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 flex flex-col items-center bottom-20 sm:bottom-24 space-y-8"
          variants={variants.content}
          initial="initial"
          animate="animate"
        >

          <motion.h1
            className="text-5xl sm:text-6xl font-extrabold font-playfair uppercase tracking-wider text-[#DBAE6E] text-center"
            style={{ textShadow: '0 6px 16px rgba(0, 0, 0, 0.7)' }}
            variants={variants.content}
          >
            Discover Timeless Elegance
          </motion.h1>
          <motion.p
            className="text-lg sm:text-xl font-light text-white dark:text-white max-w-sm sm:max-w-md text-center"
            variants={variants.content}
          >
            Laynered offers a collection of unique & premium oversized T shirts with in elegant and vibe matching designs
          </motion.p>
          <motion.button
            className="px-10 py-4 sm:px-12 sm:py-5 bg-[#DBAE6E] text-black font-semibold text-base sm:text-lg uppercase tracking-wider rounded-full flex items-center gap-4 border-2 border-[#DBAE6E] shadow-2xl focus:outline-none focus:ring-2 focus:ring-[#DBAE6E]"
            variants={variants.button}
            initial="initial"
            animate="animate"
            whileHover="hover"
            whileTap="tap"
            onClick={handleExploreClick}
            aria-label="Explore all products"
          >
            Explore Now
            <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </motion.button>
        </motion.div>
      )}

      {/* Mobile Image Indicators */}
      {isImageLoaded && (
        <div className="lg:hidden absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-4">
          {backgroundImages.map((_, index) => (
            <motion.button
              key={index}
              className={`w-4 h-4 Rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-[#DBAE6E] scale-150' : 'bg-white/60'
                }`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to image ${index + 1}`}
              whileHover={{ scale: 1.6 }}
              whileTap={{ scale: 0.8 }}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default Hero;